import { PnConsultationGlance, PnGlanceMark } from '@bariendo/lib/pn-consultation-v2-script';
import { Box, Group, Text } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { ORBIT_GRAY } from '@/theme/colors';
import { ReactNode } from 'react';

/** The timeline card canon's status hues (design-system PR #76). */
const MARK_OK = '#1F8F4D';
const MARK_BAD = '#C9201A';
const MARK_PENDING = '#D89614';

/** The canon's ● for something still open, e.g. a conditional insurance result (BAR-2998). */
export function PendingMark() {
  return (
    <Box
      aria-hidden
      style={{ width: 8, height: 8, margin: '0 3px', borderRadius: '50%', background: MARK_PENDING, flexShrink: 0 }}
    />
  );
}

function Mark({ mark }: { mark: PnGlanceMark }) {
  if (mark === 'ok') return <IconCheck size={14} stroke={2.5} color={MARK_OK} style={{ flexShrink: 0 }} />;
  if (mark === 'bad') return <IconX size={14} stroke={2.5} color={MARK_BAD} style={{ flexShrink: 0 }} />;
  if (mark === 'pending') return <PendingMark />;
  return null;
}

function Column({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <Box style={{ minWidth: 0 }}>
      <Text size="xs" c={ORBIT_GRAY} h={16} truncate>
        {label}
      </Text>
      {children}
    </Box>
  );
}

/**
 * A saved PN card's glance row (BAR-2996): Outcome · Track · Location on the
 * timeline card canon. The outcome column is twice the others, the card's one
 * change to the canon's three equal columns, so the longest outcome fits.
 */
export function PnConsultationGlanceRow({ glance }: { glance: PnConsultationGlance }) {
  // The canon's status scale: ✓ and ✗ bold, ● bold, the neutral outcome plain gray.
  const outcome = glance.outcome ?? 'No outcome recorded';
  const neutral = glance.mark === 'none' || !glance.outcome;
  return (
    <Box
      data-testid="pn-glance-row"
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)',
        columnGap: 16,
        alignItems: 'end',
      }}
    >
      <Column>
        <Group gap={6} wrap="nowrap">
          <Mark mark={glance.mark} />
          <Text
            size="sm"
            fw={neutral ? 400 : 600}
            c={glance.mark === 'bad' ? MARK_BAD : neutral ? ORBIT_GRAY : undefined}
            truncate
            title={outcome}
          >
            {outcome}
          </Text>
        </Group>
      </Column>
      <Column label="Track">
        <Text size="sm" truncate title={glance.track}>
          {glance.track}
        </Text>
      </Column>
      <Column label="Location">
        <Text size="sm" truncate c={glance.location ? undefined : ORBIT_GRAY}>
          {glance.location ?? '—'}
        </Text>
      </Column>
    </Box>
  );
}
